/* eslint-disable react/display-name */
import React, { Suspense, lazy } from 'react';
import qs from 'qs';

import PAGE_TYPES from 'tradera-constants/page-types';
import { NotFoundError } from 'tradera-components/errors';
import Loader from 'tradera-components/loader';
import StartPage from './pages/start-page'; // Startpage is included for speed
import CartPage from './pages/cart-page';
import { NewPageTracker } from 'tradera-components/new-page-tracker/new-page-tracker';
import { RouteMapRoutes } from 'tradera-components/route-map-routes/route-map-routes';

// Lacy load other pages
const BrandPage = lazy(() =>
    import(
        /* webpackChunkName: "brand-page" */ 'tradera-apps/tradera/pages/brand-page'
    )
);
const BrandListPage = lazy(() =>
    import(
        /* webpackChunkName: "brand-list-page" */ 'tradera-apps/tradera/pages/brand-list-page'
    )
);
const CampaignPage = lazy(() =>
    import(
        /* webpackChunkName: "campaign-page" */ 'tradera-apps/tradera/pages/campaign-page'
    )
);
const CategoryPage = lazy(() =>
    import(
        /* webpackChunkName: "category-page" */ 'tradera-apps/tradera/pages/category-page'
    )
);
const CheckoutPage = lazy(() =>
    import(
        /* webpackChunkName: "checkout-page" */ 'tradera-apps/tradera/pages/checkout-page'
    )
);
const CheckoutConfirmationPage = lazy(() =>
    import(
        /* webpackChunkName: "checkout-confirmation-page" */ 'tradera-apps/tradera/pages/checkout-confirmation-page'
    )
);
const DiscoverPage = lazy(() =>
    import(
        /* webpackChunkName: "discover-page" */ 'tradera-apps/tradera/pages/discover-page'
    )
);
const Landing_GantXTradera = lazy(() =>
    import(
        /* webpackChunkName: "landing_gantxtradera" */ 'tradera-apps/tradera/pages/landing/gant-x-tradera'
    )
);
const Landing_StadiumXTradera = lazy(() =>
    import(
        /* webpackChunkName: "landing-stadiumxtradera" */ 'tradera-apps/tradera/pages/landing/stadium-x-tradera'
    )
);
const ProfilePage = lazy(() =>
    import(
        /* webpackChunkName: "profile-page" */ 'tradera-apps/tradera/pages/profile-page'
    )
);
const ShippingCalculatorPage = lazy(() =>
    import(
        /* webpackChunkName: "shippingcalculator" */ 'tradera-apps/tradera/pages/shipping-calculator-page'
    )
);
const RecommendationsPage = lazy(() =>
    import(
        /* webpackChunkName: "profile-page" */ 'tradera-apps/tradera/pages/recommendations-page'
    )
);
const ReportItemPage = lazy(() =>
    import(
        /* webpackChunkName: "report-item-page" */ 'tradera-apps/tradera/pages/report-item-page'
    )
);
const VerifyPage = lazy(() =>
    import(
        /* webpackChunkName: "verify-page" */ 'tradera-apps/tradera/pages/verify-page'
    )
);

export const ViewItemPage = lazy(() =>
    import(
        /* webpackChunkName: "view-item-page" */ 'tradera-apps/tradera/pages/view-item-page'
    )
);

const routeMap = {
    startPage: {
        path: '/:lang(sv|en|de|da)?/',
        pageType: PAGE_TYPES.START_PAGE,
        strict: true,
        exact: true,
        render: props => (
            <StartPage pageType={PAGE_TYPES.START_PAGE} {...props} />
        )
    },
    viewItemPage: {
        path: '/:lang(sv|en|de|da)?/item/:categoryId/:itemId/:itemSlug',
        pageType: PAGE_TYPES.VIEW_ITEM_PAGE,
        render: props => (
            <ViewItemPage pageType={PAGE_TYPES.VIEW_ITEM_PAGE} {...props} />
        )
    },
    brandPage: {
        path: '/:lang(sv|en|de|da)?/brand/:brand',
        pageType: PAGE_TYPES.BRAND_PAGE,
        render: props => {
            const brand = props.match.params.brand;
            return <BrandPage pageType={PAGE_TYPES.BRAND_PAGE} brand={brand} />;
        }
    },
    brandListPage: {
        path: '/:lang(sv|en|de|da)?/brands/',
        pageType: PAGE_TYPES.BRAND_LIST_PAGE,
        render: props => (
            <BrandListPage pageType={PAGE_TYPES.BRAND_LIST_PAGE} {...props} />
        )
    },
    campaignPage: {
        path: [
            '/:lang(sv|en|de|da)?/campaign/:campaignOrCharityId',
            '/:lang(sv|en|de|da)?/charity/:campaignOrCharityId'
        ],
        pageType: PAGE_TYPES.CUSTOM_RESULT_PAGE,
        render: props => <CampaignPage {...props} />
    },
    categoryListPage: {
        path: '/:lang(sv|en|de|da)?/kategorier',
        pageType: PAGE_TYPES.CATEGORY_LIST,
        render: props => (
            <CategoryPage pageType={PAGE_TYPES.CATEGORY_LIST} {...props} />
        )
    },
    checkoutConfirmationPage: {
        path:
            '/:lang(sv|en|de|da)?/shopping/ordercheckout/confirmation/:purchaseOrderId',
        pageType: PAGE_TYPES.DEFAULT,
        render: props => <CheckoutConfirmationPage {...props} />
    },
    checkoutPage: {
        path: '/:lang(sv|en|de|da)?/shopping/ordercheckout/:purchaseOrderId',
        pageType: PAGE_TYPES.DEFAULT,
        render: props => (
            <CheckoutPage
                preSelectPayment={
                    qs.parse(props.location.search, {
                        ignoreQueryPrefix: true
                    }).payment
                }
                preSelectShipping={
                    qs.parse(props.location.search, {
                        ignoreQueryPrefix: true
                    }).shipping
                }
                {...props}
            />
        )
    },
    cartPage: {
        path: '/:lang(sv|en|de|da)?/shopping/cart',
        pageType: PAGE_TYPES.DEFAULT,
        render: props => <CartPage {...props} />
    },
    searchResultPage: {
        path: '/:lang(sv|en|de|da)?/search',
        pageType: PAGE_TYPES.SEARCH_RESULT_PAGE,
        render: props => (
            <DiscoverPage pageType={PAGE_TYPES.SEARCH_RESULT_PAGE} {...props} />
        )
    },
    landing_GantXTradera: {
        path: '/:lang(sv|en|de|da)?/gantxtradera',
        pageType: PAGE_TYPES.DEFAULT,
        render: props => <Landing_GantXTradera {...props} />
    },
    landing_StadiumXTradera: {
        path: '/:lang(sv|en|de|da)?/stadiumxtradera',
        pageType: PAGE_TYPES.DEFAULT,
        render: props => <Landing_StadiumXTradera {...props} />
    },
    profilePage: {
        path: '/:lang(sv|en|de|da)?/profile/items',
        pageType: PAGE_TYPES.CUSTOM_RESULT_PAGE,
        render: props => (
            <ProfilePage pageType={PAGE_TYPES.CUSTOM_RESULT_PAGE} {...props} />
        )
    },
    recommendationsPage: {
        path:
            '/:lang(sv|en|de|da)?/ma/endless-recommendation/:recommendationModeId(selected|endingSoon)',
        pageType: PAGE_TYPES.ENDLESS_RECOMMENDATIONS_PAGE,
        render: props => (
            <RecommendationsPage
                pageType={PAGE_TYPES.ENDLESS_RECOMMENDATIONS_PAGE}
                {...props}
            />
        )
    },
    reportItemPage: {
        path: '/:lang(sv|en|de|da)?/report-item/:itemId',
        pageType: PAGE_TYPES.REPORT_ITEM_PAGE,
        render: props => (
            <ReportItemPage pageType={PAGE_TYPES.REPORT_ITEM_PAGE} {...props} />
        )
    },
    verifyPage: {
        path: '/:lang(sv|en|de|da)?/verify/bankid',
        pageType: PAGE_TYPES.DEFAULT,
        render: props => (
            <VerifyPage
                // TODO: Move into VerifyPage component
                returnUrl={
                    qs.parse(props.location.search, {
                        ignoreQueryPrefix: true
                    }).returnUrl
                }
                {...props}
            />
        )
    },
    shippingCalculatorPage: {
        path: '/:lang(sv|en|de|da)?/fraktkalkylatorn',
        pageType: PAGE_TYPES.DEFAULT,
        render: props => <ShippingCalculatorPage {...props} />
    },
    categoryResultPage: {
        path: '/:lang(sv|en|de|da)?/:category',
        pageType: PAGE_TYPES.CATEGORY_RESULT_PAGE,
        exact: true,
        render: props => (
            <DiscoverPage
                pageType={PAGE_TYPES.CATEGORY_RESULT_PAGE}
                {...props}
            />
        )
    },
    notFoundPage: {
        path: '*',
        pageType: PAGE_TYPES.ERROR_PAGE,
        exact: true,
        render: () => <NotFoundError />
    }
};

const Routes = () => {
    return (
        <Suspense fallback={<Loader usePortal={false} />}>
            <NewPageTracker routeMap={routeMap} />
            <RouteMapRoutes routeMap={routeMap} />
        </Suspense>
    );
};

export default Routes;
